import React, { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Users } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { GENERATE_LOOKALIKE_HYPOTHESES, LOOKALIKE_OPTIONS } from '@/graphql/lookalike-queries'
import {
    accountAudienceRetentionText,
    accountAudienceSizeText,
    accountAudienceSubtypeLabel,
} from '@/utils/account-audiences'
import { isAiAudienceObjective } from '@/utils/audience-builder'
import {
    ACCOUNT_SEED_KIND,
    LOOKALIKE_TEXT,
    buildLookalikeInputs,
    chosenSeed,
    isLookalikeSelected,
    isSameSeed,
    isLookalikeStale,
    lookalikeAccountSeedLabel,
    lookalikeSeedLabel,
    lookalikeSeedLine,
    mapLookalikeError,
    selectedLookalikes,
    withLookalikes,
} from '@/utils/lookalike-audiences'

interface LookalikeSeed {
    seed_kind: string
    label: string
    quality_verified: boolean
    status: string
    approximate_count_lower: number | null
    eligible: boolean
    message: string | null
}

interface LookalikeAccountSeed {
    account_audience_id: string
    label: string
    subtype: string
    retention_days: number | null
    status: string
    approximate_count_lower: number | null
    eligible: boolean
    message: string | null
}

interface SeedChoice {
    kind: string | null
    accountAudienceId: string | null
}

interface LookalikeInputs {
    campaignId?: string
    publisherId?: string
    objective: string
    location: string
}

interface LookalikePanelProps {
    inputs: LookalikeInputs
    data: Record<string, any>
    onChange: (data: Record<string, any>) => void
    isEditMode?: boolean
}

/**
 * The lookalike option of the acquisition stage. Shown only when the server offers lookalikes for the licence
 * and objective; seeds and their sizes come from reads only, and Meta creates the lookalike only when the
 * campaign is published. A lookalike carried over from a duplicated campaign, or prepared for other campaign
 * details, is removed, so it is never sent without being shown.
 */
const LookalikePanel: React.FC<LookalikePanelProps> = ({ inputs, data, onChange, isEditMode = false }) => {
    const { campaignId, publisherId, objective, location } = inputs
    const enabled = !isEditMode && !!campaignId && !!publisherId && isAiAudienceObjective(objective)
    const currentInputs = buildLookalikeInputs({ publisherId, objective, location })

    const { data: queryData } = useQuery(LOOKALIKE_OPTIONS, {
        variables: { campaignId, publisherId, objective: String(objective || '').toLowerCase() },
        fetchPolicy: 'network-only',
        skip: !enabled,
    })
    const [generate, { loading }] = useMutation(GENERATE_LOOKALIKE_HYPOTHESES)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const latest = useRef({ data, onChange })
    latest.current = { data, onChange }

    const result = queryData?.AudienceHypothesis?.lookalikeOptions
    const seeds: LookalikeSeed[] = result?.available ? result.seeds || [] : []
    const eligibleSeeds = seeds.filter((seed) => seed.eligible)
    const accountSeeds: LookalikeAccountSeed[] = result?.available ? result.account_seeds || [] : []
    const eligibleAccountSeeds = accountSeeds.filter((seed) => seed.eligible)
    const selected = isLookalikeSelected(data)
    const stale = selectedLookalikes(data).some((entry: Record<string, any>) => isLookalikeStale(entry, currentInputs))
    const notOffered = enabled && !!result && !result.available && selected
    const [seedChoice, setSeedChoice] = useState<SeedChoice>(chosenSeed(data))
    const registrySeed = (kind: string) => ({ kind, accountAudienceId: null })
    const accountSeed = (id: string) => ({ kind: ACCOUNT_SEED_KIND, accountAudienceId: id })
    const activeChoice: SeedChoice = seedChoice.kind
        ? seedChoice
        : eligibleSeeds[0]
          ? registrySeed(eligibleSeeds[0].seed_kind)
          : eligibleAccountSeeds[0]
            ? accountSeed(eligibleAccountSeeds[0].account_audience_id)
            : { kind: null, accountAudienceId: null }
    const activeAccountSeed = activeChoice.accountAudienceId
        ? accountSeeds.find((seed) => seed.account_audience_id === activeChoice.accountAudienceId)
        : undefined
    const activeSeed = activeChoice.accountAudienceId
        ? undefined
        : seeds.find((seed) => seed.seed_kind === activeChoice.kind)

    useEffect(() => {
        if (notOffered || stale) onChange(withLookalikes(data, []))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notOffered, stale])

    if (!enabled || !result?.available) return null

    const prepare = async (next: SeedChoice) => {
        if (!next.kind || loading) return
        setErrorMessage(null)
        try {
            const response = await generate({
                variables: {
                    campaignId,
                    publisherId,
                    objective: String(objective || '').toLowerCase(),
                    location,
                    ...(next.accountAudienceId
                        ? { accountAudienceId: next.accountAudienceId }
                        : { seedKind: next.kind }),
                },
            })
            const hypotheses = response?.data?.generateLookalikeHypotheses
            if (!Array.isArray(hypotheses) || hypotheses.length === 0) throw new Error('empty result')
            latest.current.onChange(withLookalikes(latest.current.data, hypotheses, currentInputs))
        } catch (error) {
            setErrorMessage(mapLookalikeError(error))
        }
    }

    const toggle = (checked: boolean) => {
        if (checked) prepare(activeChoice)
        else onChange(withLookalikes(data, []))
    }

    const chooseSeed = (next: SeedChoice) => {
        setSeedChoice(next)
        if (selected && !isSameSeed(next, chosenSeed(data))) prepare(next)
    }

    const switchId = 'lookalike-audience'
    const countryUnknown = result.reason === 'country_unknown'
    const hasEligibleSeed = eligibleSeeds.length > 0 || eligibleAccountSeeds.length > 0
    const canAdd = !countryUnknown && hasEligibleSeed
    return (
        <div
            className="tw:mx-6 tw:flex tw:gap-3 tw:flex-col tw:bg-secondary-background tw:rounded-xs tw:p-3"
            data-testid="lookalike-panel"
        >
            <div className="tw:flex tw:gap-1.5 tw:items-center">
                <Users className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">{LOOKALIKE_TEXT.title}</span>
                <span className="tw:text-sm tw:text-gray-200">(optional)</span>
            </div>
            <p className="tw:text-sm tw:text-gray-200">{LOOKALIKE_TEXT.description}</p>
            {countryUnknown && (
                <p className="tw:text-sm tw:text-yellow-300" role="status">
                    {LOOKALIKE_TEXT.countryUnknown}
                </p>
            )}
            {!countryUnknown && !hasEligibleSeed && (
                <p className="tw:text-sm tw:text-yellow-300" role="status">
                    {LOOKALIKE_TEXT.noSeed}
                </p>
            )}
            <div className="tw:flex tw:items-center tw:gap-2">
                <Switch
                    id={switchId}
                    checked={selected}
                    disabled={loading || (!selected && !canAdd)}
                    onCheckedChange={toggle}
                />
                <label htmlFor={switchId} className="tw:text-sm tw:font-medium tw:cursor-pointer">
                    {LOOKALIKE_TEXT.switchLabel}
                </label>
                {loading && (
                    <span className="tw:text-xs tw:text-gray-200" role="status">
                        Preparing the lookalike
                    </span>
                )}
            </div>
            <fieldset className="tw:flex tw:flex-col tw:gap-2" aria-label="Seed audience">
                {seeds.map((seed) => (
                    <label
                        key={seed.seed_kind}
                        className="tw:flex tw:items-start tw:gap-2 tw:text-sm"
                        data-testid={`lookalike-seed-${seed.seed_kind}`}
                    >
                        <input
                            type="radio"
                            name="lookalike-seed"
                            value={seed.seed_kind}
                            checked={isSameSeed(activeChoice, registrySeed(seed.seed_kind))}
                            disabled={!seed.eligible || countryUnknown || loading}
                            onChange={() => chooseSeed(registrySeed(seed.seed_kind))}
                            className="tw:mt-1"
                        />
                        <span className="tw:flex tw:flex-col">
                            <span>{lookalikeSeedLabel(seed)}</span>
                            <span className="tw:text-xs tw:text-gray-200">{lookalikeSeedLine(seed)}</span>
                        </span>
                    </label>
                ))}
            </fieldset>
            {accountSeeds.length > 0 && (
                <fieldset
                    className="tw:flex tw:flex-col tw:gap-2"
                    aria-label={LOOKALIKE_TEXT.accountGroupTitle}
                    data-testid="lookalike-account-seeds"
                >
                    <span className="tw:text-sm tw:font-medium">{LOOKALIKE_TEXT.accountGroupTitle}</span>
                    {eligibleAccountSeeds.length === 0 && (
                        <p className="tw:text-xs tw:text-gray-200" data-testid="lookalike-account-empty">
                            {LOOKALIKE_TEXT.accountEmpty}
                        </p>
                    )}
                    {eligibleAccountSeeds.map((seed) => {
                        const retention = accountAudienceRetentionText(seed.retention_days)
                        return (
                            <label
                                key={seed.account_audience_id}
                                className="tw:flex tw:items-start tw:gap-2 tw:text-sm"
                                data-testid={`lookalike-account-seed-${seed.account_audience_id}`}
                            >
                                <input
                                    type="radio"
                                    name="lookalike-seed"
                                    value={seed.account_audience_id}
                                    checked={isSameSeed(activeChoice, accountSeed(seed.account_audience_id))}
                                    disabled={countryUnknown || loading}
                                    onChange={() => chooseSeed(accountSeed(seed.account_audience_id))}
                                    className="tw:mt-1"
                                />
                                <span className="tw:flex tw:flex-col tw:gap-1">
                                    <span>{lookalikeAccountSeedLabel(seed)}</span>
                                    <span className="tw:flex tw:flex-wrap tw:gap-1.5 tw:text-xs tw:text-gray-200">
                                        <span data-testid="lookalike-account-seed-type">
                                            {accountAudienceSubtypeLabel(seed.subtype)}
                                        </span>
                                        <span data-testid="lookalike-account-seed-size">
                                            {accountAudienceSizeText(seed.approximate_count_lower)}
                                        </span>
                                        {retention && (
                                            <span data-testid="lookalike-account-seed-retention">{retention}</span>
                                        )}
                                    </span>
                                </span>
                            </label>
                        )
                    })}
                </fieldset>
            )}
            {(result.ranges || []).length > 0 && result.country && (
                <span className="tw:text-xs tw:text-gray-200" data-testid="lookalike-range">
                    {`Top ${result.ranges.join(' and ')} of people in ${result.country}`}
                </span>
            )}
            {activeAccountSeed && (
                <p className="tw:text-xs tw:text-yellow-300" data-testid="lookalike-quality-note">
                    {LOOKALIKE_TEXT.accountQualityNote}
                </p>
            )}
            {activeSeed && !activeSeed.quality_verified && (
                <p className="tw:text-xs tw:text-yellow-300" data-testid="lookalike-quality-note">
                    {LOOKALIKE_TEXT.qualityNote}
                </p>
            )}
            {errorMessage && (
                <p className="tw:text-sm tw:text-red-300" role="alert">
                    {errorMessage}
                </p>
            )}
            <p className="tw:text-xs tw:text-gray-200">{LOOKALIKE_TEXT.creationNote}</p>
        </div>
    )
}

export default LookalikePanel
